import * as React from 'react';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import { cn } from '../../lib/utils.ts';

function Page({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="page" className={cn('space-y-6', className)} {...props} />
  );
}

function PageHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="page-header" className={cn('', className)} {...props} />
  );
}

function PageTitle({ className, ...props }: React.ComponentProps<'h1'>) {
  return (
    <h1
      data-slot="page-title"
      className={cn('text-3xl font-bold text-foreground', className)}
      {...props}
    />
  );
}

function PageDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="page-description"
      className={cn('mt-2 text-muted-foreground', className)}
      {...props}
    />
  );
}

function PageContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-content"
      className={cn('space-y-6', className)}
      {...props}
    />
  );
}

function PageSection({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="page-section" className={cn('', className)} {...props} />
  );
}

function PageGrid({
  className,
  cols = 1,
  ...props
}: React.ComponentProps<'div'> & {
  cols?: 1 | 2 | 3 | 4;
}) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div
      data-slot="page-grid"
      className={cn('grid gap-6', gridCols[cols], className)}
      {...props}
    />
  );
}

function PageLoading({
  className,
  message = 'Caricamento...',
  ...props
}: React.ComponentProps<'div'> & {
  message?: string;
}) {
  return (
    <div
      data-slot="page-loading"
      className={cn('text-center py-12', className)}
      {...props}
    >
      <div className="animate-spin rounded-pill h-12 w-12 border-b-2 border-primary-strong mx-auto"></div>
      <p className="mt-4 text-muted-foreground">{message}</p>
    </div>
  );
}

function PageEmpty({
  className,
  title = 'Nessun elemento trovato',
  description,
  illustration,
  action,
  ...props
}: React.ComponentProps<'div'> & {
  title?: string;
  description?: string;
  /**
   * Brand illustration (manual §15). An empty state is one of the few moments
   * with room for the mascotte, and a blank panel is the weakest thing a
   * screen can show. Decorative: mark the image `alt=""` at the call site.
   */
  illustration?: React.ReactNode;
  /** Primary CTA — the way out of the empty state. */
  action?: React.ReactNode;
}) {
  return (
    <div
      data-slot="page-empty"
      className={cn(
        'flex flex-col items-center py-8 text-center',
        illustration && 'gap-1',
        className,
      )}
      {...props}
    >
      {illustration}
      <p className={cn('text-muted-foreground', illustration && 'mt-2')}>
        {title}
      </p>
      {description && (
        <p className="text-sm text-muted-foreground/70 mt-2">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

function PageBackLink({
  className,
  children,
  ...props
}: React.ComponentProps<'button'>) {
  return (
    <button
      data-slot="page-back-link"
      className={cn(
        'mt-1 flex items-center px-4 py-2 border rounded-control gap-1 text-sm text-muted-foreground',
        'hover:text-foreground transition-colors cursor-pointer',
        className,
      )}
      {...props}
    >
      <ArrowLeft className="h-4 w-4" />
      {children}
    </button>
  );
}

export {
  Page,
  PageBackLink,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
  PageSection,
  PageGrid,
  PageLoading,
  PageEmpty,
};
